import { Link } from "@inertiajs/react";
import type { ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/helpers/currency-formatter";
import { purchaseRequestPath, quotePath } from "@/routes";
import { formatDateAR } from "@/utils/date";

export interface PriceHistoryRecord {
  price_type: string;
  product_id: number;
  product_internal_code: string | null;
  product_name: string;
  unit_price: string;
  currency: string;
  supplier_id: number;
  supplier_name: string;
  purchase_request_id: number;
  purchase_request_number: string;
  quote_id: number;
  recorded_at: string;
}

export const columns: ColumnDef<PriceHistoryRecord>[] = [
  {
    accessorKey: "product_internal_code",
    header: "Código",
    enableSorting: false,
    cell: ({ row }) => {
      const code = row.original.product_internal_code;
      return code ? (
        <span className="font-mono text-sm">{code}</span>
      ) : (
        <span className="text-muted-foreground">-</span>
      );
    },
  },
  {
    accessorKey: "product_name",
    header: "Producto",
    enableSorting: false,
    cell: ({ row }) => {
      const productName = row.original.product_name;
      return (
        <div className="max-w-xs">
          <span className="block truncate font-medium" title={productName}>
            {productName}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "unit_price",
    header: "Precio Unitario",
    enableSorting: false,
    cell: ({ row }) => {
      const price = parseFloat(row.original.unit_price);
      const currency = row.original.currency;
      const isUSD = currency === "USD";
      return (
        <div className="text-center font-mono">
          {isUSD && "USD "}{formatCurrency(price, currency)}
        </div>
      );
    },
  },
  {
    accessorKey: "supplier_name",
    header: "Proveedor",
    enableSorting: false,
    cell: ({ row }) => {
      const supplierName = row.original.supplier_name;
      return (
        <div className="max-w-xs">
          <span className="block truncate" title={supplierName}>
            {supplierName}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "price_type",
    header: "Tipo",
    enableSorting: false,
    cell: ({ row }) => {
      const type = row.original.price_type;
      const variant = type === "Cotizado" ? "secondary" : "default";
      return <Badge variant={variant}>{type}</Badge>;
    },
  },
  {
    accessorKey: "recorded_at",
    header: "Fecha",
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <span className="text-sm text-muted-foreground">
          {formatDateAR(row.original.recorded_at)}
        </span>
      );
    },
  },
  {
    accessorKey: "purchase_request_number",
    header: "SC",
    enableSorting: false,
    cell: ({ row }) => {
      const pr = row.original;
      return (
        <Link
          href={purchaseRequestPath(pr.purchase_request_id)}
          className="text-sm text-primary hover:underline"
        >
          {pr.purchase_request_number}
        </Link>
      );
    },
  },
  {
    accessorKey: "quote_id",
    header: "Cotización",
    enableSorting: false,
    cell: ({ row }) => {
      const quote = row.original;
      return (
        <Link
          href={quotePath(quote.quote_id)}
          className="text-sm text-primary hover:underline"
        >
          #{quote.quote_id}
        </Link>
      );
    },
  },
];
